import FixedPodcastPlayer from "@features/podcasts/components/FixedPodcastPlayer";
import usePodcastPlayer from "@features/podcasts/hooks/usePodcastPlayer";
import { usePodcasts } from "@features/podcasts/hooks/usePodcasts";
import { useSavedPodcastsCache } from "@features/podcasts/hooks/useSavedPodcastsCache";
import { Citations } from "@features/session-config/components/Citations";
import { handleNavigateBack } from "@helpers/common";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";

import Box from "@memorang/ui/src/components/Box";
import { ZoomImage } from "@memorang/ui/src/components/MarkdownRenderer/ZoomImage";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
import { useToast } from "@memorang/ui/src/components/Toast";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { useLocalSearchParams } from "expo-router";
import { useLayoutEffect, useMemo } from "react";
import { Appbar, Chip, Text } from "react-native-paper";
import type { Track } from "react-native-track-player";

const PodcastScreen = () => {
	const { podcastId } = useLocalSearchParams();
	const theme = useAppTheme();
	const { loadTracks } = usePodcastPlayer();
	const { isMobile } = useDevice();
	const { podcasts } = usePodcasts();
	const { isPodcastSaved, toggleSavedPodcast } = useSavedPodcastsCache();
	const { showToast } = useToast();

	const currentPodcastIndex = useMemo(
		() => podcasts.findIndex((a) => a.id === Number(podcastId)),
		[podcasts, podcastId],
	);

	const podcast = podcasts[currentPodcastIndex]!;

	useLayoutEffect(() => {
		const tracks: Track[] = podcasts.map((podcast) => ({
			url: podcast.metadata.podcast,
			title: podcast.content.title,
			artist: podcast.metadata.author,
			artwork: podcast.metadata.thumbnail,
			album: podcast.id.toString(),
		}));
		loadTracks({
			tracks,
			currentPodcastIndex,
			currentTrack: tracks[currentPodcastIndex],
			shouldReset: false,
		});
	}, [loadTracks, podcasts, currentPodcastIndex]);

	const handleBookmarkToggle = () => {
		toggleSavedPodcast(Number(podcastId));
		const message = isPodcastSaved(Number(podcastId))
			? "Removed from bookmarks"
			: "Added to bookmarks";
		showToast({
			message,
			action: {
				label: "Undo",
				action: () => {
					toggleSavedPodcast(Number(podcastId));
				},
			},
		});
	};

	return (
		<>
			<Appbar.Header
				mode="medium"
				style={{ backgroundColor: theme.colors.elevation.level3 }}
			>
				<Appbar.BackAction onPress={() => handleNavigateBack("/podcasts")} />
				<Appbar.Content title={podcast.content.title} />
				<Appbar.Action
					icon={
						isPodcastSaved(Number(podcastId)) ? "bookmark" : "bookmark-outline"
					}
					onPress={handleBookmarkToggle}
				/>
			</Appbar.Header>
			<ScrollContainer
				contentContainerStyle={{
					paddingTop: isMobile ? 16 : 32,
					paddingHorizontal: 16,
					paddingBottom: 150,
				}}
			>
				<Box gap={16}>
					<Box width="100%" paddingHorizontal={16} alignItems="center">
						<ZoomImage
							url={podcast.metadata.thumbnail}
							allImages={[
								{
									url: podcast.metadata.thumbnail,
									caption: podcast.content.title,
								},
							]}
							width={240}
							height={240}
							borderRadius={8}
						/>
					</Box>
					<Box gap={16}>
						<FixedPodcastPlayer />
					</Box>
					<Box gap={10}>
						<Box
							flexDirection="row"
							justifyContent="space-between"
							alignItems="center"
						>
							<Box>
								<Text
									variant="labelMedium"
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									{podcast.metadata.author}
								</Text>
								<Text variant="headlineSmall" numberOfLines={2}>
									{podcast.content.title}
								</Text>
							</Box>
						</Box>
						{podcast.tags && podcast.tags.length > 0 && (
							<Box flexDirection="row" flexWrap="wrap" gap={8}>
								{podcast.tags.map((tag) => (
									<Chip
										compact
										key={`tag-${podcast.id}-${tag}`}
										textStyle={{
											fontSize: 12,
										}}
									>
										{tag.label}
									</Chip>
								))}
							</Box>
						)}
						{/* {podcast.metadata.date && (
								<Text variant="labelSmall">
									Published date: {podcast.metadata.date}
								</Text>
							)} */}
						<Box paddingTop={8}>
							<CustomMarkdownRenderer
								text={podcast.content.article}
								customStyles={{
									paragraph: {
										lineHeight: 24,
										marginBottom: 16,
									},
								}}
							/>
						</Box>
						<Citations
							useBottomSheetFlatList={false}
							useWebColors
							citations={[
								{
									pageContent: podcast.content.article,
									title: podcast.content.title,
									url: podcast.metadata.sourceUrl,
									id: podcast.id,
								},
							]}
						/>
					</Box>
				</Box>
			</ScrollContainer>
		</>
	);
};

export default PodcastScreen;
